import { UserName } from "@/components/UserName";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useRoles, useRoleNames } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { OneTimeReveal } from "@/components/OneTimeReveal";
import { ConfirmAction, resetTotpConfirm, deactivateConfirm } from "@/components/ConfirmAction";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BackLink } from "@/components/BackLink";
import {
  ArrowLeft, Loader2, ShieldCheck, ShieldAlert, UserX, UserCheck, KeyRound, RotateCcw,
  Lock, Save, ListChecks, Clock, AlertTriangle, GraduationCap, TrendingUp,
} from "lucide-react";


const fmt = (s) => (s ? String(s).slice(0, 19).replace("T", " ") : "—");

export default function UserDetailPage() {
  const roles = useRoles();
  const roleNames = useRoleNames();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me, has } = useAuth();
  const canGrant = has("manage_roles_permissions");
  const [u, setU] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "", home_site_id: "" });
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [reveal, setReveal] = useState(null); // {kind, value|codes}
  const [signal, setSignal] = useState(null);
  const [gradBusy, setGradBusy] = useState(false);
  // GR-40: the SAME confirmation box the Users list uses, so the two can never drift again.
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setU(data);
      setForm({ name: data.name || "", email: data.email || "", role: data.role || "", home_site_id: data.home_site_id || "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); navigate("/users"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/sites").then(({ data }) => setSites(data || [])).catch(() => {}); }, []);

  // Private accuracy signal — informs the graduation decision, admin-only.
  const loadSignal = useCallback(async () => {
    try { const { data } = await api.get(`/admin/users/${id}/review-signal`); setSignal(data); }
    catch { setSignal(null); }
  }, [id]);
  useEffect(() => { loadSignal(); }, [loadSignal]);

  const setGraduation = async (graduated) => {
    setGradBusy(true);
    try {
      await api.post(`/admin/users/${id}/review-graduation`, { graduated });
      toast.success(graduated ? "Graduated — this person now finalizes without mandatory review." : "Put back into mandatory review.");
      await load(); await loadSignal();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGradBusy(false); }
  };

  const dirty = u && (form.name !== (u.name || "") || form.email !== (u.email || "") || form.role !== u.role || (form.home_site_id || "") !== (u.home_site_id || ""));
  const isSelf = u && me?.email === u.email;

  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/users/${id}`, form); toast.success("Saved"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (action, label) => {
    setBusy(action);
    try { await api.post(`/admin/users/${id}/${action}`); toast.success(label); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const resetPassword = async () => {
    if (isSelf) return;
    if (!window.confirm(`Reset password for ${u.email}? Their sessions end and they must set a new password next login. You'll see a one-time temporary password to relay.`)) return;
    setBusy("reset-password");
    try {
      const { data } = await api.post(`/admin/users/${id}/reset-password`);
      setReveal({ kind: "password", value: data.temp_password });
      // Deliberately NO refresh here — see OneTimeReveal.jsx. It happens on close.
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const closeReveal = () => { setReveal(null); load(); };

  /* GR-40 — the SAME shared box as the Users list, via named handlers (see the note there). */
  const askResetTotp = () => setPending(resetTotpConfirm(u, isSelf, () => act("reset-totp", "2FA reset — re-enroll next login")));
  const askDeactivate = () => setPending(deactivateConfirm(u, () => act("deactivate", "User deactivated — sessions ended")));

  const genBackupCodes = async () => {
    if (!window.confirm(`Generate new 2FA backup codes for ${u.email}? Any previous codes stop working. Shown once.`)) return;
    setBusy("backup-codes");
    try {
      const { data } = await api.post(`/admin/users/${id}/backup-codes`);
      setReveal({ kind: "codes", codes: data.codes });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!u) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="user-detail-page" className="max-w-4xl space-y-6">
      <BackLink to="/users" label="Users" testid="back-to-users" />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="detail-name">{u.name}</h1>
          <p className="text-sm text-slate-500 font-mono">{u.email}</p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <Badge className={`rounded-lg ${u.role === "Admin" ? "bg-[#1F1F1F] text-white" : "bg-slate-200 text-slate-700"}`} data-testid="detail-role-badge">{u.role}</Badge>
          {u.active ? <Badge className="rounded-lg bg-green-600 text-white" data-testid="detail-status-badge">ACTIVE</Badge>
            : <Badge className="rounded-lg bg-destructive text-white" data-testid="detail-status-badge">DEACTIVATED</Badge>}
          {u.totp_enrolled ? <Badge className="rounded-lg bg-emerald-700 text-white"><ShieldCheck className="w-3 h-3 mr-1" />2FA on</Badge>
            : <Badge variant="outline" className="rounded-lg border-amber-400 text-amber-600"><ShieldAlert className="w-3 h-3 mr-1" />2FA pending</Badge>}
          {u.must_change_password && <Badge variant="outline" className="rounded-lg border-[#E0781A] text-[#C76714]" data-testid="detail-mustchange-badge">must change password</Badge>}
          {u.review_graduated && <Badge className="rounded-lg bg-emerald-700 text-white" data-testid="detail-graduated-badge"><GraduationCap className="w-3 h-3 mr-1" />review-exempt</Badge>}
        </div>
      </div>

      {/* meta */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        {[["Created", fmt(u.created_at)], ["Created by", u.created_by ? <UserName email={u.created_by} inline /> : "—"], ["Last login", fmt(u.last_login_at)], ["Last updated", fmt(u.updated_at)]].map(([k, v]) => (
          <div key={k} className="border border-slate-200 bg-white rounded-lg p-3">
            <div className="font-mono uppercase tracking-wider text-[10px] text-slate-400">{k}</div>
            <div className="text-slate-800 mt-0.5 break-all">{v}</div>
          </div>
        ))}
      </div>

      {/* edit profile */}
      <div className="border border-slate-200 bg-white rounded-lg p-5">
        <div className="font-display font-bold mb-3">Profile</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label>
            <Input data-testid="edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border-slate-200 mt-1" />
            {!form.name.trim() && <p data-testid="edit-name-required" className="text-[11px] text-[#B01E28] mt-1">Name is required.</p>}</div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
            <Input data-testid="edit-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border-slate-200 mt-1 font-mono" /></div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="edit-role" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="Choose a role" /></SelectTrigger>
              <SelectContent className="rounded-lg">
                {roles.map((r) => <SelectItem key={r} value={r} data-testid={`role-opt-${r.replace(/ /g, "-").toLowerCase()}`}>{roleNames[r] || r}</SelectItem>)}
                {/* A user still carrying a retired role (e.g. "basic") must SHOW it, or the trigger
                    renders empty and an admin can't see what they're changing — the same trap the
                    retired donor stages set. Same treatment: visible, selectable, labelled. */}
                {form.role && !roles.includes(form.role) && (
                  <SelectItem value={form.role} data-testid="role-opt-retired">
                    {roleNames[form.role] || form.role} — not a current role
                  </SelectItem>
                )}
              </SelectContent>
            </Select></div>
          <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Home site</Label>
            <Select value={form.home_site_id || "none"} onValueChange={(v) => setForm({ ...form, home_site_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="edit-home-site" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="All sites (no home)" /></SelectTrigger>
              <SelectContent className="rounded-lg">
                <SelectItem value="none">All sites (no home)</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">Scopes their Put-Away queue to this yard. "All sites" sees every yard's backlog.</p></div>
        </div>
        <Button data-testid="save-profile" disabled={!dirty || saving || !form.name.trim()} onClick={save} className="rounded-lg bg-[#1F1F1F] text-white mt-4">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save changes
        </Button>
      </div>

      {/* access controls */}
      <div className="border border-slate-200 bg-white rounded-lg p-5">
        <div className="font-display font-bold mb-1">Access &amp; security</div>
        <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4" data-testid="detail-password-note">
          <Lock className="w-3.5 h-3.5 shrink-0" /> Passwords are hashed and <b className="mx-1">never viewable by anyone</b>. You can only reset — restoring access in seconds without ever seeing a password.
        </div>
        <div className="flex flex-wrap gap-2">
          {u.active
            ? <Button data-testid="detail-deactivate" variant="outline" disabled={busy === "deactivate" || u.role === "Admin" || isSelf}
                className="rounded-lg border-slate-200 text-destructive"
                onClick={askDeactivate}>
                <UserX className="w-4 h-4 mr-1" />Deactivate</Button>
            : <Button data-testid="detail-activate" variant="outline" disabled={busy === "activate"}
                className="rounded-lg border-slate-200 text-green-700" onClick={() => act("activate", "User reactivated")}>
                <UserCheck className="w-4 h-4 mr-1" />Activate</Button>}
          <Button data-testid="detail-reset-password" variant="outline" disabled={busy === "reset-password" || isSelf} className="rounded-lg border-slate-200 text-[#C76714]" onClick={resetPassword}
            title={isSelf
              ? "Not for your own account — it would end your session before you could save the temporary password. Use My Password & 2FA Codes to change your own."
              : "Reset password"}>
            {busy === "reset-password" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}Reset password</Button>
          <Button data-testid="detail-reset-2fa" variant="outline" disabled={busy === "reset-totp"} className="rounded-lg border-slate-200"
            onClick={askResetTotp}>
            <KeyRound className="w-4 h-4 mr-1" />Reset 2FA</Button>
          <Button data-testid="detail-backup-codes" variant="outline" disabled={busy === "backup-codes"} className="rounded-lg border-slate-200" onClick={genBackupCodes}>
            {busy === "backup-codes" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ListChecks className="w-4 h-4 mr-1" />}Generate backup codes</Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          2FA backup codes — {u.backup_codes?.generated ? <span><b>{u.backup_codes.remaining}</b> of {u.backup_codes.total} remaining (generated {fmt(u.backup_codes.generated_at)})</span> : "none generated yet"}.
        </p>
      </div>

      {/* PER-PERSON EXCEPTIONS. The grid is per ROLE; this is the one person. The enforcement
          side already existed on every request — what was missing was any way to write it for a
          capability other than review graduation, so "grant it to just him" meant granting it to
          everyone sharing his role. */}
      {u.role !== "Admin" && canGrant && <PermissionExceptions u={u} onSaved={load} />}

      {/* review readiness & graduation — private accuracy signal + the manual toggle it
          informs. Shown for review-eligible roles (Admin bypasses review entirely). */}
      {u.role !== "Admin" && (
        <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="review-graduation-card">
          <div className="font-display font-bold mb-1 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-emerald-700" />Review readiness &amp; graduation</div>
          <p className="text-xs text-slate-500 mb-4">Graduating removes this person from mandatory admin review — they finalize parts straight to Available. A judgment call; the signal below is advisory only.</p>

          {/* accuracy signal */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-4" data-testid="accuracy-signal">
            {!signal ? (
              <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading signal…</div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-2"><TrendingUp className="w-3.5 h-3.5" />Accuracy signal · last {signal.window_days} days</div>
                <div className="flex flex-wrap gap-5">
                  <div><div className="text-2xl font-black text-slate-800" data-testid="signal-reviewed">{signal.reviewed}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">reviewed</div></div>
                  <div><div className="text-2xl font-black text-slate-800" data-testid="signal-corrected">{signal.corrected}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">corrected</div></div>
                  <div><div className="text-2xl font-black text-slate-800" data-testid="signal-rate">{signal.correction_rate_pct}%</div><div className="text-[10px] uppercase tracking-wider text-slate-400">correction rate</div></div>
                </div>
                <div data-testid="signal-readiness" className={`mt-3 flex items-start gap-2 rounded-md px-3 py-2 text-xs ${
                  signal.readiness.level === "consider" ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                    : signal.readiness.level === "keep_reviewing" ? "bg-amber-50 text-amber-800 border border-amber-200"
                      : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span><b>Advisory:</b> {signal.readiness.message} <span className="opacity-70">The decision is yours.</span></span>
                </div>
              </>
            )}
          </div>

          {/* toggle */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm">
              {u.review_graduated
                ? <span className="text-emerald-700 font-semibold flex items-center gap-1"><ShieldCheck className="w-4 h-4" />Graduated — finalizes without review</span>
                : <span className="text-slate-600 font-semibold flex items-center gap-1"><ShieldAlert className="w-4 h-4 text-amber-600" />In mandatory review</span>}
            </div>
            {u.review_graduated
              ? <Button data-testid="ungraduate-user" variant="outline" disabled={gradBusy} onClick={() => { if (window.confirm(`Put ${u.email} back into mandatory review?`)) setGraduation(false); }} className="rounded-lg border-slate-200 text-slate-700 ml-auto">
                  {gradBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}Put back into review</Button>
              : <Button data-testid="graduate-user" disabled={gradBusy} onClick={() => { if (window.confirm(`Graduate ${u.email} out of mandatory review? They'll finalize parts straight to Available.`)) setGraduation(true); }} className="rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white ml-auto">
                  {gradBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <GraduationCap className="w-4 h-4 mr-1" />}Graduate from review</Button>}
          </div>
        </div>
      )}

      {/* recent activity */}
      <div className="border border-slate-200 bg-white rounded-lg p-5">
        <div className="font-display font-bold mb-3 flex items-center gap-2"><Clock className="w-4 h-4" />Recent activity</div>
        {u.recent_logins?.length > 0 && (
          <div className="mb-4">
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-1">Login history</div>
            <div className="space-y-1">
              {u.recent_logins.map((l, i) => (
                <div key={i} className="flex items-center gap-2 text-xs" data-testid={`login-row-${i}`}>
                  {l.success ? <ShieldCheck className="w-3.5 h-3.5 text-green-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-destructive" />}
                  <span className="font-mono text-slate-500">{fmt(l.timestamp)}</span>
                  <span className={l.success ? "text-green-700" : "text-destructive"}>{l.success ? "success" : (l.reason || "failed")}</span>
                  <span className="text-slate-400 font-mono">{l.ip || ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-1">Audit trail</div>
        {u.activity?.length ? (
          <div className="space-y-1" data-testid="audit-activity">
            {u.activity.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="font-mono text-slate-500">{fmt(a.timestamp)}</span>
                <Badge variant="outline" className="rounded text-[10px] border-slate-300">{a.action}</Badge>
                <span className="text-slate-400 font-mono truncate">by {a.actor_email}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-slate-400">No recorded activity yet.</p>}
      </div>

      {/* ONE shared reveal for every shown-once secret in the app — see OneTimeReveal.jsx.
          Covers BOTH kinds here: the temporary password and the backup codes. Neither refreshes
          anything behind itself; the refresh happens on close. */}
      <OneTimeReveal
        open={!!reveal}
        title={reveal?.kind === "codes" ? "2FA backup codes" : "Temporary password"}
        intro={`Share with ${u.email} securely.` + (reveal?.kind === "codes"
          ? " Each code works once, in place of their authenticator code."
          : " They must set their own password at next login.")}
        value={reveal?.kind === "password" ? reveal.value : undefined}
        codes={reveal?.kind === "codes" ? reveal.codes : undefined}
        onClose={closeReveal}
        testid="reveal-dialog" />

      {/* THE SAME confirmation component the Users list uses (ConfirmAction.jsx), including the
          typed-RESET gate on your own account and the wording. One implementation, two pages. */}
      <ConfirmAction pending={pending} onClose={() => setPending(null)} />
    </div>
  );
}

// ---------------------------------------------------------------- PER-PERSON EXCEPTIONS
// One capability, one person, without changing their role. Forty-odd capabilities is a wall if
// you list them all, so the list starts as whatever exceptions are already set and opens up when
// you search. Each row states the ROLE DEFAULT beside the three states, because "granted" only
// means something against what the role already does.
function PermissionExceptions({ u, onSaved }) {
  const [caps, setCaps] = useState([]);
  const [roleGrid, setRoleGrid] = useState({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/permissions")
      .then(({ data }) => { setCaps(data.capabilities || []); setRoleGrid((data.grid || {})[u.role] || {}); })
      .catch(() => {});
  }, [u.role]);

  const ov = u.perm_overrides || {};
  const ql = q.trim().toLowerCase();
  const rows = caps.filter((c) => c.key in ov
    || (ql.length >= 2 && ((c.label || "").toLowerCase().includes(ql) || c.key.includes(ql))));

  const set = async (cap, state) => {
    setBusy(cap + state);
    try {
      await api.put(`/admin/users/${u.id}/permission-override`, { capability: cap, state });
      toast.success(state === "default" ? "Exception removed — this person follows their role again"
        : state === "grant" ? "Granted to this person only" : "Blocked for this person only");
      await onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const StateBtn = ({ cap, state, label }) => {
    const cur = cap in ov ? (ov[cap] ? "grant" : "block") : "default";
    const on = cur === state;
    return (
      <button type="button" data-testid={`ovr-${state}-${cap}`} disabled={!!busy}
        onClick={() => !on && set(cap, state)}
        className={`px-2.5 py-1 rounded-md border text-[11px] font-semibold transition-colors disabled:opacity-50 ${
          on ? (state === "grant" ? "border-emerald-500 bg-emerald-50 text-emerald-700"
            : state === "block" ? "border-[#B01E28] bg-red-50 text-[#B01E28]"
              : "border-slate-400 bg-slate-100 text-slate-700")
            : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>
        {label}
      </button>
    );
  };

  return (
    <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="permission-exceptions-card">
      <div className="font-display font-bold mb-1">Just for this person</div>
      <p className="text-xs text-slate-500 mb-3">
        An exception to their <b>{u.role_name || u.role}</b> role, for this account only — the role itself is untouched and
        everyone else in it is unaffected. Takes effect on their next page load. Every change is audited.
      </p>
      <Input data-testid="ovr-search" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search a capability to grant or block (e.g. health, cost, scrap)…" className="rounded-lg border-slate-200 mb-3" />
      <div className="space-y-1.5">
        {rows.length === 0 && (
          <p data-testid="ovr-empty" className="text-xs text-slate-400">
            {ql.length >= 2 ? "No capability matches those words." : "No exceptions set — this person follows their role exactly. Search above to add one."}
          </p>
        )}
        {rows.map((c) => (
          <div key={c.key} data-testid={`ovr-row-${c.key}`} className="rounded-lg border border-slate-200 px-3 py-2.5">
            <div className="text-sm text-slate-800">{c.label}</div>
            <div className="text-[11px] text-slate-400 font-mono">
              {c.key} · role default: <b className={roleGrid[c.key] ? "text-emerald-700" : "text-slate-500"}>{roleGrid[c.key] ? "ON" : "OFF"}</b>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <StateBtn cap={c.key} state="default" label="Follow role" />
              <StateBtn cap={c.key} state="grant" label="Grant to them" />
              <StateBtn cap={c.key} state="block" label="Block for them" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
